// src/components/emailMarketing/EmailEditor.jsx
import React, { useMemo, useRef, useCallback, useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './EmailEditor.css';

// Variable global para rastrear si ya hemos interceptado los warnings
let warningsSuppressed = false;
let originalWarn = null;
let originalError = null;

// Interceptar warnings de findDOMNode antes de que React los muestre
const suppressFindDOMNodeWarnings = () => {
    if (!warningsSuppressed) {
        originalWarn = console.warn;
        originalError = console.error;
        warningsSuppressed = true;
    }
    
    const suppressWarning = (...args) => {
        // Convertir todos los argumentos a string para buscar
        const message = args.map(arg => {
            if (typeof arg === 'string') return arg;
            if (typeof arg === 'object' && arg !== null) {
                try {
                    return JSON.stringify(arg);
                } catch {
                    return String(arg);
                }
            }
            return String(arg);
        }).join(' ');
        
        // Suprimir cualquier warning que contenga findDOMNode o ReactQuill con findDOMNode
        if (
            message.includes('findDOMNode') ||
            message.includes('find-node') ||
            message.includes('findDOMNode is deprecated') ||
            message.includes('will be removed in the next major release') ||
            (message.includes('ReactQuill') && (message.includes('deprecated') || message.includes('findDOMNode')))
        ) {
            return; // Suprimir el warning
        }
        
        // Mostrar otros warnings normalmente
        if (originalWarn) {
            originalWarn.apply(console, args);
        }
    };
    
    console.warn = suppressWarning;
    
    return () => {
        // No restaurar inmediatamente, solo cuando todos los componentes se desmonten
        // Esto evita que otros componentes muestren el warning
    };
};

// Restaurar warnings cuando todos los componentes EmailEditor se desmonten
const restoreWarnings = () => {
    if (warningsSuppressed && originalWarn) {
        console.warn = originalWarn;
        console.error = originalError;
        warningsSuppressed = false;
    }
};

// Wrapper para suprimir el warning de findDOMNode de ReactQuill
const QuillWrapper = React.forwardRef((props, ref) => {
    // Suprimir el warning de findDOMNode durante el montaje del componente
    useEffect(() => {
        const restore = suppressFindDOMNodeWarnings();
        return restore;
    }, []);
    
    return <ReactQuill ref={ref} {...props} />;
});

QuillWrapper.displayName = 'QuillWrapper';

function EmailEditor({ value, onChange, placeholder }) {
    const quillRef = useRef(null);
    
    // Suprimir warnings de findDOMNode al montar el componente
    useEffect(() => {
        suppressFindDOMNodeWarnings();
        // No restaurar inmediatamente al desmontar para evitar que otros componentes muestren el warning
        // Se restaurará cuando la página se recargue o cuando todos los componentes se desmonten
    }, []);
    
    // Callback para manejar el ref de manera más segura
    const quillRefCallback = useCallback((el) => {
        quillRef.current = el;
    }, []);

    // Configuración de módulos del editor
    const modules = useMemo(() => ({
        toolbar: {
            container: [
                [{ 'header': [1, 2, 3, false] }],
                ['bold', 'italic', 'underline', 'strike'],
                [{ 'color': [] }, { 'background': [] }],
                [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                [{ 'align': [] }],
                ['link', 'image'],
                ['clean'],
                ['code-block']
            ],
        },
        clipboard: {
            matchVisual: false,
        }
    }), []);

    // Formato permitido
    const formats = [
        'header',
        'bold', 'italic', 'underline', 'strike',
        'color', 'background',
        'list', 'bullet',
        'align',
        'link', 'image',
        'code-block'
    ];

    // Manejar inserción de placeholders usando la API de Quill
    const handlePlaceholderInsert = (placeholder) => {
        if (quillRef.current) {
            const quill = quillRef.current.getEditor();
            const range = quill.getSelection();
            const index = range ? range.index : quill.getLength();
            quill.insertText(index, placeholder, 'user');
            quill.setSelection(index + placeholder.length);
        }
    };

    // Variables organizadas por categorías
    const variables = {
        contacto: [
            { label: 'Nombre', value: '{{contact.name}}', description: 'Nombre del contacto' },
            { label: 'Email', value: '{{contact.email}}', description: 'Email del contacto' },
            { label: 'Baja', value: '{{unsubscribe.url}}', description: 'Enlace de baja (GDPR requerido)' }
        ],
        marca: [
            { label: 'Nombre', value: '{{marca.name}}', description: 'Nombre de la marca' },
            { label: 'Logo URL', value: '{{marca.logoUrl}}', description: 'URL del logo de la marca' },
            { label: 'Email', value: '{{marca.email}}', description: 'Email de la marca' },
            { label: 'Teléfono', value: '{{marca.phone}}', description: 'Teléfono/WhatsApp de la marca' },
            { label: 'Website', value: '{{marca.website}}', description: 'Sitio web de la marca' },
            { label: 'Dirección', value: '{{marca.direccion}}', description: 'Dirección física de la marca' },
            { label: 'Horarios', value: '{{marca.horarios}}', description: 'Horarios de atención' },
            { label: 'Facebook', value: '{{marca.facebook}}', description: 'URL de Facebook' },
            { label: 'Instagram', value: '{{marca.instagram}}', description: 'URL de Instagram' },
            { label: 'Twitter', value: '{{marca.twitter}}', description: 'URL de Twitter/X' },
            { label: 'LinkedIn', value: '{{marca.linkedin}}', description: 'URL de LinkedIn' },
            { label: 'TikTok', value: '{{marca.tiktok}}', description: 'URL de TikTok' },
            { label: 'YouTube', value: '{{marca.youtube}}', description: 'URL de YouTube' },
            { label: 'WhatsApp', value: '{{marca.whatsapp}}', description: 'Número o enlace de WhatsApp' }
        ],
        sistema: [
            { label: 'Año', value: '{{year}}', description: 'Año actual' }
        ]
    };

    return (
        <div className="email-editor-wrapper">
            {/* Barra de placeholders expandida */}
            <div className="placeholder-toolbar mb-2 p-3 bg-gray-50 rounded border border-gray-200">
                <div className="mb-2">
                    <span className="text-xs font-semibold text-gray-700">Variables disponibles:</span>
                </div>
                
                {/* Variables de Contacto */}
                <div className="mb-2">
                    <span className="text-xs text-gray-600 font-medium mb-1 block">Contacto:</span>
                    <div className="flex flex-wrap gap-1">
                        {variables.contacto.map((item) => (
                            <button
                                key={item.value}
                                type="button"
                                onClick={() => handlePlaceholderInsert(item.value)}
                                className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                                title={item.description}
                            >
                                {item.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Variables de Marca */}
                <div className="mb-2">
                    <span className="text-xs text-gray-600 font-medium mb-1 block">Marca:</span>
                    <div className="flex flex-wrap gap-1">
                        {variables.marca.map((item) => (
                            <button
                                key={item.value}
                                type="button"
                                onClick={() => handlePlaceholderInsert(item.value)}
                                className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
                                title={item.description}
                            >
                                {item.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Variables de Sistema */}
                <div>
                    <span className="text-xs text-gray-600 font-medium mb-1 block">Sistema:</span>
                    <div className="flex flex-wrap gap-1">
                        {variables.sistema.map((item) => (
                            <button
                                key={item.value}
                                type="button"
                                onClick={() => handlePlaceholderInsert(item.value)}
                                className="px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded hover:bg-purple-200 transition-colors"
                                title={item.description}
                            >
                                {item.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Editor Quill */}
            <QuillWrapper
                ref={quillRefCallback}
                theme="snow"
                value={value || ''}
                onChange={onChange}
                modules={modules}
                formats={formats}
                placeholder={placeholder || 'Escribe el contenido del email aquí...'}
                className="email-quill-editor"
            />

            {/* Advertencia si falta unsubscribe */}
            {value && !value.includes('{{unsubscribe.url}}') && (
                <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                    ⚠️ <strong>Importante:</strong> Debes incluir el enlace de baja {'{{unsubscribe.url}}'} para cumplir con GDPR.
                </div>
            )}
        </div>
    );
}

export default EmailEditor;

